import { useState, useEffect, useMemo } from "react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { api, fmtMoney, fmtMoneyCents } from "@/lib/api";
import { useSettings } from "@/lib/settings";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Info } from "lucide-react";

const calc = (price, dpPct, apr, term) => {
  const down = price * (dpPct / 100);
  const principal = price - down;
  const r = apr / 100 / 12;
  const monthly = r === 0 ? principal / term : (principal * r) / (1 - Math.pow(1 + r, -term));
  const total = monthly * term;
  return {
    down, principal,
    monthly: isFinite(monthly) ? monthly : 0,
    total: isFinite(total) ? total : 0,
    interest: isFinite(total) ? total - principal : 0,
  };
};

export const FinancingCalculator = ({ initialPrice = 45000, vehicleName, onApply, compact }) => {
  const settings = useSettings();
  const [price, setPrice] = useState(initialPrice);
  const [dpPct, setDpPct] = useState(10);
  const [term, setTerm] = useState(48);
  const [apr, setApr] = useState(7.9);

  useEffect(() => {
    if (settings) {
      setDpPct((d) => Math.max(d, settings.min_down_payment_pct));
      setApr(settings.apr);
      setTerm(settings.max_term);
    }
  }, [settings]);

  useEffect(() => setPrice(initialPrice), [initialPrice]);

  const minDp = settings?.min_down_payment_pct ?? 10;
  const maxTerm = settings?.max_term ?? 48;
  const terms = settings?.available_terms ?? [12, 18, 24, 30, 36, 42, 48];
  const dpOptions = settings?.down_payment_options ?? [10, 15, 20, 25, 30, 35, 40];

  const result = useMemo(() => calc(price, dpPct, apr, term), [price, dpPct, apr, term]);
  const chartData = [
    { name: "Principal", value: Math.round(result.principal) },
    { name: "Interest", value: Math.round(result.interest) },
  ];
  const COLORS = ["#2E8BFF", "#DCE7FA"];

  return (
    <div className="card-surface p-6 sm:p-8" data-testid="financing-calculator">
      <div className="mb-6 flex items-center justify-between">
        <h3 className="font-display text-xl font-bold text-slate-900">Financing Calculator</h3>
        <span className="rounded-full bg-gold/10 px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-gold">Estimate</span>
      </div>

      <div className="space-y-6">
        {!compact && (
          <div>
            <label className="mb-2 flex items-center justify-between text-sm text-slate-700">
              <span>Vehicle Price</span>
              <span className="font-mono font-semibold text-slate-900">{fmtMoney(price)}</span>
            </label>
            <Input
              data-testid="calc-price-input"
              type="number"
              value={price}
              onChange={(e) => setPrice(Math.max(0, Number(e.target.value)))}
              className="border-slate-200 bg-[#EEF3FC] text-slate-900"
            />
          </div>
        )}

        <div>
          <label className="mb-2 flex items-center justify-between text-sm text-slate-700">
            <span>Down Payment</span>
            <span className="font-mono font-semibold text-gold">{dpPct}% · {fmtMoney(result.down)}</span>
          </label>
          <Slider
            data-testid="financing-calculator-down-payment-slider"
            min={minDp} max={60} step={1}
            value={[dpPct]}
            onValueChange={([v]) => setDpPct(Math.max(minDp, v))}
            className="py-2"
          />
          <div className="mt-3 flex flex-wrap gap-2">
            {dpOptions.map((o) => (
              <button
                key={o}
                data-testid={`calc-dp-${o}`}
                onClick={() => setDpPct(o)}
                className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                  dpPct === o ? "border-gold bg-gold text-[#EEF3FC]" : "border-slate-200 text-slate-700 hover:border-gold/40"
                }`}
              >{o}%</button>
            ))}
          </div>
          <p className="mt-2 text-[11px] text-slate-500">Minimum down payment is {minDp}%. You may choose a higher amount.</p>
        </div>

        <div>
          <label className="mb-2 block text-sm text-slate-700">Financing Term (max {maxTerm} months)</label>
          <div className="flex flex-wrap gap-2">
            {terms.map((t) => (
              <button
                key={t}
                data-testid={`calc-term-${t}`}
                onClick={() => setTerm(t)}
                className={`rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
                  term === t ? "border-gold bg-gold text-[#EEF3FC]" : "border-slate-200 text-slate-700 hover:border-gold/40"
                }`}
              >{t}mo</button>
            ))}
          </div>
        </div>

        <div>
          <label className="mb-2 flex items-center justify-between text-sm text-slate-700">
            <span>APR</span>
            <span className="font-mono font-semibold text-slate-900">{apr}%</span>
          </label>
          <Slider min={0} max={25} step={0.1} value={[apr]} onValueChange={([v]) => setApr(v)} className="py-2" />
        </div>
      </div>

      <div className="mt-8 grid grid-cols-1 items-center gap-6 rounded-xl border border-slate-200 bg-[#EEF3FC] p-6 sm:grid-cols-2">
        <div className="relative h-40">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={chartData} innerRadius={48} outerRadius={70} paddingAngle={2} dataKey="value" stroke="none">
                {chartData.map((e, i) => <Cell key={i} fill={COLORS[i]} />)}
              </Pie>
              <Tooltip
                contentStyle={{ background: "#FFFFFF", border: "1px solid rgba(2,32,71,0.12)", borderRadius: 8, color: "#0F172A" }}
                formatter={(v) => fmtMoney(v)}
              />
            </PieChart>
          </ResponsiveContainer>
          <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-[10px] uppercase tracking-wider text-slate-500">Financed</span>
            <span className="font-display text-sm font-bold text-slate-900">{fmtMoney(result.principal)}</span>
          </div>
        </div>
        <div>
          <p className="text-xs uppercase tracking-wider text-slate-500">Estimated Monthly Payment</p>
          <p data-testid="calc-monthly-output" className="font-display text-4xl font-extrabold text-gold">{fmtMoneyCents(result.monthly)}</p>
          <div className="mt-3 space-y-1 text-xs text-slate-600">
            <div className="flex justify-between"><span>Amount Financed</span><span className="font-mono text-slate-700">{fmtMoney(result.principal)}</span></div>
            <div className="flex justify-between"><span>Total Interest</span><span className="font-mono text-slate-700">{fmtMoney(result.interest)}</span></div>
            <div className="flex justify-between"><span>Total of Payments</span><span className="font-mono text-slate-700">{fmtMoney(result.total)}</span></div>
          </div>
        </div>
      </div>

      <div className="mt-4 flex items-start gap-2 rounded-lg bg-slate-100 p-3 text-[11px] leading-relaxed text-slate-600">
        <Info className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-gold" />
        <span>Results are estimates only and do not constitute a financing offer or approval. Final terms are subject to a formally approved financing agreement.</span>
      </div>

      {onApply && (
        <Button
          data-testid="calc-prequalify-btn"
          onClick={() => onApply({ down_payment_pct: dpPct, term, apr, vehicle_price: price })}
          className="btn-gold mt-5 w-full rounded-full py-6 text-base"
        >
          Prequalify for this Rate
        </Button>
      )}
    </div>
  );
};
